/**
 * Metering & entitlements foundation — the declarative metric registry + the
 * single entitlement resolver. Canonical design: docs/billing/metering-and-entitlements.md.
 *
 * This is the *evolutive* surface: adding a future quota = ONE entry in
 * METRIC_REGISTRY (§15 of the doc). The gate, admin form, dashboard, and Stripe
 * pack reconstruction all derive from this map.
 *
 * Phase 1 scope (current): the registry + resolveEntitlement, with defaultByTier
 * mirroring the legacy TIER_LIMITS for the overlapping metrics so there is zero
 * behavior change. Enforcement wiring (gauges/meters/reserve/commit) lands in
 * later phases — see the doc's §17.
 */

import type { Tier } from './tenant';
import type { Cadence } from './cadence';

/** The five enforcement shapes. Every metric is exactly one. See doc §2. */
export type MetricKind = 'gauge' | 'meter' | 'allowlist' | 'scalar' | 'boolean';

export interface MetricDef {
  /** Stable key — also the Stripe pack `metadata.metric` value. Never rename in place. */
  key: string;
  kind: MetricKind;

  /** Meters only: counter reset period. 'balance' = no reset (credits). */
  period?: 'day' | 'month' | 'balance';

  /**
   * Default limit per tier. Semantics by kind:
   *  - gauge / meter / scalar: a number. -1 = unlimited (still clamped by systemCeiling).
   *  - allowlist: the `max` count; the permitted set default is `allowlistDefaults`.
   *  - boolean: 0 = off, 1 = on.
   */
  defaultByTier: Record<Tier, number>;

  /** allowlist only: the default permitted set per tier. */
  allowlistDefaults?: Partial<Record<Tier, string[]>>;

  /**
   * Hard ceiling, tier-independent. Effective limit is always
   * min(tierDefault + packs + override, systemCeiling). Protects the app from
   * runaway/misconfigured limits. -1 only if genuinely unbounded.
   */
  systemCeiling: number;

  /** Can upsell packs grant more of this? (gauge/meter only.) */
  addonStackable: boolean;

  unit: string;
  label: { fr: string; en: string };

  /** Dashboard / admin-form grouping. */
  group?: string;
}

// Primary = real consumer products (registry name `chatgpt` ↔ run-path `openai`).
const ALL_ENGINES = ['chatgpt', 'gemini', 'claude', 'perplexity'];
// Secondary = simulated engines (a model + a Serper web-search harness we own),
// selectable by agency + enterprise only, run only when explicitly chosen. Same
// id in registry and run path (no alias). See docs/runbooks/adding-bp-engines.md.
//   mistral     → Bedrock Mistral + a Serper search loop ≈ Le Chat
//   ai_overview → Serper SERP + page-1 <main> + Gemini ≈ Google AI Overview
const SECONDARY_ENGINES = ['mistral', 'ai_overview'];
const ALL_ENGINES_WITH_SECONDARY = [...ALL_ENGINES, ...SECONDARY_ENGINES];

/**
 * The metric registry. Numbers mirror the agreed 5-tier table (basic as the
 * floor, monotonic up, enterprise/agency unlimited or ceiling). The authoritative
 * commercial values live in docs/billing/pricing-and-billing.md §2; keep in sync.
 */
export const METRIC_REGISTRY: Record<string, MetricDef> = {
  // ---------------- gauges (stock — count of live things) ----------------
  brands: {
    key: 'brands', kind: 'gauge', addonStackable: true, unit: 'marques',
    defaultByTier: { basic: 1, pro: 1, elite: 3, enterprise: -1, agency: -1 },
    systemCeiling: 200, label: { fr: 'Marques', en: 'Brands' }, group: 'identity',
  },
  // Counted TENANT-WIDE (shared pool across all brands). systemCeiling is the
  // tenant-wide runaway cap, not a per-brand limit — at 50 it was clamping
  // agency/enterprise (-1) to 50 zones across their ENTIRE book of brands.
  zones: {
    key: 'zones', kind: 'gauge', addonStackable: true, unit: 'zones',
    defaultByTier: { basic: 1, pro: 3, elite: 10, enterprise: -1, agency: -1 },
    systemCeiling: 1000, label: { fr: 'Zones', en: 'Zones' }, group: 'identity',
  },
  languages: {
    key: 'languages', kind: 'gauge', addonStackable: true, unit: 'langues',
    defaultByTier: { basic: 1, pro: 2, elite: 5, enterprise: -1, agency: -1 },
    systemCeiling: 20, label: { fr: 'Langues', en: 'Languages' }, group: 'identity',
  },
  // Shared across the 3 BP axes (supersedes the per-axis query caps; the gauge
  // is wired into the create path in metering Phase 2).
  prompts: {
    key: 'prompts', kind: 'gauge', addonStackable: true, unit: 'prompts',
    // Agency is credit-metered (real spend bounded by the credit balance); the
    // 1000 here is only a SOFT runaway-prevention cap, not a commercial limit —
    // raise it per-agency via quotaOverrides on request.
    defaultByTier: { basic: 30, pro: 100, elite: 200, enterprise: -1, agency: 1000 },
    systemCeiling: 2000, label: { fr: 'Prompts', en: 'Prompts' }, group: 'brand_perception',
  },
  // Counted TENANT-WIDE (shared pool across all brands), like prompts/zones.
  // systemCeiling is the tenant-wide runaway cap — it must NOT be the per-brand
  // matrix DOM cap (that is COMPETITOR_HARD_CAP=100, enforced separately per
  // brand in CompetitorsService.add), otherwise agency/enterprise (-1) would be
  // clamped to 100 competitors across ALL brands.
  competitors: {
    key: 'competitors', kind: 'gauge', addonStackable: true, unit: 'concurrents',
    defaultByTier: { basic: 3, pro: 10, elite: 50, enterprise: -1, agency: -1 },
    systemCeiling: 1000, label: { fr: 'Concurrents suivis', en: 'Tracked competitors' }, group: 'competitors',
  },
  users: {
    key: 'users', kind: 'gauge', addonStackable: true, unit: 'utilisateurs',
    defaultByTier: { basic: 1, pro: 2, elite: 10, enterprise: -1, agency: 10 },
    systemCeiling: 500, label: { fr: 'Utilisateurs', en: 'Users' }, group: 'identity',
  },
  // Product module — INDEXED CATALOGUE CAPACITY: the sum of persisted items
  // (products + category pages) across EVERY catalogue of EVERY brand of the
  // tenant. It's the capacity the module is sold on, so it's a gauge, not a
  // meter: deleting a catalogue frees the slots back.
  //
  // Every tier defaults to 0 — the Product module is admin-gated
  // (featureFlags.productModuleEnabled), so capacity is bought (a self-serve
  // `catalog_items` pack) or negotiated (quotaOverrides.catalog_items for
  // enterprise). Agency is credit-metered on the RUNS, so its capacity is
  // deliberately not the throttle — CATALOGUE_MAX_ITEMS (50k/catalogue) already
  // bounds a single ingest, and the reserve bounds the spend.
  catalog_items: {
    key: 'catalog_items', kind: 'gauge', addonStackable: true, unit: 'produits indexés',
    defaultByTier: { basic: 0, pro: 0, elite: 0, enterprise: 0, agency: -1 },
    systemCeiling: 2_000_000,
    label: { fr: 'Produits indexés', en: 'Indexed products' }, group: 'product',
  },
  // Total tasks per tenant (all statuses). enterprise = -1 (negotiated via
  // quotaOverrides.tasks); agency = a high system-protection default, raisable
  // per-agency via quotaOverrides (an override REPLACES the default and is
  // clamped to systemCeiling, so the ceiling must stay ≥ any agency value).
  tasks: {
    key: 'tasks', kind: 'gauge', addonStackable: true, unit: 'tâches',
    defaultByTier: { basic: 3, pro: 30, elite: 100, enterprise: -1, agency: 10000 },
    systemCeiling: 10000, label: { fr: 'Tâches', en: 'Tasks' }, group: 'tasks',
  },

  // ---- Cibles (Targets) — two gauges with deliberately different scopes ----
  //
  // `establishments` is TENANT-WIDE: every establishment costs a Geoapify credit
  // from a 3 000/day platform-wide budget shared across all tenants. The cap is on
  // geocoding spend, not on matcher memory. Default 100 on every tier; raisable per
  // tenant via quotaOverrides.establishments (negotiated, told us in advance). D34.
  establishments: {
    key: 'establishments', kind: 'gauge', addonStackable: false, unit: 'établissements',
    defaultByTier: { basic: 100, pro: 100, elite: 100, enterprise: 100, agency: 100 },
    // Commercial ceiling — set well above any value we would negotiate.
    systemCeiling: 50_000,
    label: { fr: 'Établissements', en: 'Establishments' }, group: 'targets',
  },
  // `target_signals` is PER BRAND — the ONLY per-brand gauge in the registry.
  //
  // ⚠ NEVER sum target_signals across brands. One index is loaded per brand in
  // the search Lambda (D39); a tenant-wide pool would give an agency's 15 brands
  // 33k signals each, which destroys the feature. The per-brand cap matches the
  // one container allocation it actually protects. D13 + docs/billing/metering-and-entitlements.md §12.9.
  //
  // systemCeiling is an ENGINEERING number, not commercial: what a container can
  // actually load. Set from Lot 3's load test; ~40 bytes/signal × 500k = ~20 MB.
  // Raising it past the measured limit is a Lambda-memory change, not an admin toggle.
  //
  // The admin quota view MUST show the MAX across the tenant's brands, not a sum
  // (D13). usedByMetric in metering.controller.ts handles this specially.
  target_signals: {
    key: 'target_signals', kind: 'gauge', addonStackable: false, unit: 'signaux',
    defaultByTier: { basic: 500_000, pro: 500_000, elite: 500_000, enterprise: 500_000, agency: 500_000 },
    // Engineering ceiling — set from Lot 3 load test. Placeholder pending measurement.
    systemCeiling: 2_000_000,
    label: { fr: 'Signaux cibles', en: 'Target signals' }, group: 'targets',
  },

  // ---------------- allowlist (set membership) ----------------
  engines: {
    key: 'engines', kind: 'allowlist', addonStackable: true, unit: 'modèles',
    // agency + enterprise counts include the secondary engines so the gauge
    // doesn't block selecting them (6 = 4 primary + 2 secondary today).
    defaultByTier: { basic: 2, pro: 4, elite: 4, enterprise: 10, agency: 6 },
    allowlistDefaults: {
      basic: ['chatgpt', 'gemini'],
      pro: ALL_ENGINES,
      elite: ALL_ENGINES,
      enterprise: ALL_ENGINES_WITH_SECONDARY, // up to 10 customizable via override
      agency: ALL_ENGINES_WITH_SECONDARY,
    },
    systemCeiling: 10, label: { fr: 'Modèles IA', en: 'AI models' }, group: 'brand_perception',
  },

  // ---------------- scalars (per-action values) ----------------
  pages_per_website_audit: {
    key: 'pages_per_website_audit', kind: 'scalar', addonStackable: false, unit: 'pages',
    defaultByTier: { basic: 10, pro: 30, elite: 100, enterprise: -1, agency: -1 },
    systemCeiling: 500, label: { fr: 'Pages / audit site', en: 'Pages / website audit' }, group: 'website',
  },
  fetch_per_earned_media: {
    key: 'fetch_per_earned_media', kind: 'scalar', addonStackable: false, unit: 'fetch',
    defaultByTier: { basic: 30, pro: 60, elite: 100, enterprise: -1, agency: -1 },
    systemCeiling: 300, label: { fr: 'Fetch / earned media', en: 'Fetch / earned media' }, group: 'earned_media',
  },
  data_retention_days: {
    key: 'data_retention_days', kind: 'scalar', addonStackable: false, unit: 'jours',
    defaultByTier: { basic: 90, pro: 180, elite: 365, enterprise: -1, agency: 365 },
    systemCeiling: 1095, label: { fr: 'Conservation des données', en: 'Data retention' }, group: 'billing',
  },

  // ---------------- meters (flow — per period / per balance) ----------------
  content_creations: {
    key: 'content_creations', kind: 'meter', period: 'month', addonStackable: true, unit: 'créations',
    defaultByTier: { basic: 1, pro: 5, elite: 20, enterprise: -1, agency: -1 },
    systemCeiling: 1000, label: { fr: 'Créations de contenu', en: 'Content creations' }, group: 'website',
  },
  // Product module — OPTIMIZATION REVIEWS: one unit per catalogue item (product
  // OR category page) pushed through the optimize pipeline. A MONTHLY balance
  // that resets like content_creations, because the cost is per LLM run: a
  // RE-optimization costs exactly the same as the first one (same fetch + same
  // Nemotron call), so it consumes a fresh unit rather than being free.
  //
  // Consumed by the whole run at once at optimize.start (the run size is known
  // up front), so the gate is `used + itemCount > limit` — a run that doesn't
  // fit is rejected whole rather than half-billed. Agency is exempt (-1): it
  // pays in credits via the reserve. Every self-serve tier defaults to 0 —
  // capacity is bought as a pack alongside catalog_items, or negotiated
  // (quotaOverrides.product_reviews). Enterprise is 0 for the same reason
  // catalog_items is: the module is unlocked per deal, so BOTH numbers are set
  // explicitly at tenant creation — an unset override must not mean "unlimited".
  product_reviews: {
    key: 'product_reviews', kind: 'meter', period: 'month', addonStackable: true, unit: 'optimisations',
    defaultByTier: { basic: 0, pro: 0, elite: 0, enterprise: 0, agency: -1 },
    systemCeiling: 2_000_000,
    label: { fr: 'Optimisations produits', en: 'Product optimizations' }, group: 'product',
  },
  // AGENCY ONLY — the unit of all agency consumption. Granted via Stripe, not a
  // tier default, so every tier defaults to 0.
  credit: {
    key: 'credit', kind: 'meter', period: 'balance', addonStackable: true, unit: 'crédits',
    defaultByTier: { basic: 0, pro: 0, elite: 0, enterprise: 0, agency: 0 },
    systemCeiling: -1, label: { fr: 'Crédits', en: 'Credits' }, group: 'billing',
  },

  // ---------------- booleans (feature on/off) ----------------
  api_access: {
    key: 'api_access', kind: 'boolean', addonStackable: false, unit: '',
    defaultByTier: { basic: 0, pro: 0, elite: 1, enterprise: 1, agency: 1 },
    systemCeiling: 1, label: { fr: 'Accès API / MCP', en: 'API / MCP access' }, group: 'billing',
  },
  premium_support: {
    key: 'premium_support', kind: 'boolean', addonStackable: false, unit: '',
    defaultByTier: { basic: 0, pro: 0, elite: 1, enterprise: 1, agency: 1 },
    systemCeiling: 1, label: { fr: 'Support premium', en: 'Premium support' }, group: 'billing',
  },
  news_watch_proposals: {
    key: 'news_watch_proposals', kind: 'scalar', addonStackable: false, unit: 'propositions',
    defaultByTier: { basic: 1, pro: 3, elite: 5, enterprise: 5, agency: -1 },
    systemCeiling: 50, label: { fr: 'Propositions News Watch', en: 'News Watch proposals' }, group: 'news_watch',
  },
  reports: {
    key: 'reports', kind: 'boolean', addonStackable: false, unit: '',
    defaultByTier: { basic: 0, pro: 0, elite: 0, enterprise: 0, agency: 1 },
    systemCeiling: 1, label: { fr: 'Rapports', en: 'Reports' }, group: 'billing',
  },
};

/**
 * One row of the tenant usage summary (GET /metering/usage). `limit === -1`
 * means unlimited; `used` is the live count; `label`/`unit` come from the
 * registry so the UI doesn't re-derive them.
 */
export interface UsageMetric {
  metric: string;
  used: number;
  limit: number;
  label: { fr: string; en: string };
  unit: string;
}

/**
 * Tenant-level quota usage summary surfaced on Settings → Marques. Tenant-wide
 * gauges only (brands/zones/languages/prompts/competitors/users/tasks). Credits
 * (agency balance) + per-brand cadence are returned alongside via their own
 * shapes by the controller, not in this list.
 */
export interface MeteringUsageSummary {
  tier: string;
  metrics: UsageMetric[];
}

/**
 * Per-brand feature usage — surfaced as professional cards for AGENCY tenants
 * (which have no commercial quota to display, just what each brand uses). The
 * counts are pure usage (no limit), and cadence is the effective per-axis
 * schedule. Tier-display tenants use `MeteringUsageSummary` instead.
 */
export interface BrandUsage {
  brandId: string;
  brandName: string;
  url?: string;
  prompts: number;
  zones: number;
  languages: number;
  competitors: number;
  tasks: number;
  /**
   * Cibles signals held by THIS brand. The only per-brand gauge (D13) — the tenant-wide
   * `target_signals` row in `metrics` carries the MAX of these, never their sum, so this
   * is the number the admin UI must show next to the cap.
   */
  targetSignals?: number;
  /** Cibles établissements on this brand; the `establishments` metric sums them (D34). */
  establishments?: number;
  /** Effective cadence per schedulable axis (brand override → tier default). */
  cadence: Record<string, Cadence>;
}

/**
 * A resolved effective limit for one tenant × one metric. Computed, never stored.
 * `used`/`reserved`/`available` are filled by the metering service at read time
 * (left undefined by the pure resolver).
 */
export interface Entitlement {
  metric: string;
  kind: MetricKind;
  limit: number;
  allowed?: string[];
  used?: number;
  reserved?: number;
  available?: number;
}

const UNLIMITED = -1;

/** Clamp `raw` to a ceiling, honoring -1 (unlimited) on both sides. */
function clampToCeiling(raw: number, ceiling: number): number {
  if (raw === UNLIMITED && ceiling === UNLIMITED) return UNLIMITED;
  if (raw === UNLIMITED) return ceiling; // unlimited request, finite ceiling → ceiling
  if (ceiling === UNLIMITED) return raw;
  return Math.min(raw, ceiling);
}

/**
 * Resolve the effective limit for one metric. Resolution order (doc §4.1):
 *
 *   base     = registry.defaultByTier[tier]
 *   packs    = Σ active addon packs for this metric (additive, only if addonStackable)
 *   override = quotaOverrides[metric] — if set, REPLACES base+packs (negotiated truth)
 *   raw      = override ?? (base === -1 ? -1 : base + packs)
 *   limit    = min(raw, systemCeiling)   // ceiling always clamps (invariant #5)
 *
 * Unknown metric → throws (programmer error). Unknown tier → falls back to
 * `basic` semantics by reading defaultByTier['basic'] (the resolver never throws
 * on tier, mirroring resolveLimits — it can run in always-rendered UI).
 */
export function resolveEntitlement(
  metric: string,
  tier: string,
  opts?: { packs?: number; override?: number },
): Entitlement {
  const def = METRIC_REGISTRY[metric];
  if (!def) {
    throw new Error(`Unknown metric: ${metric}`);
  }

  const base = def.defaultByTier[tier as Tier] ?? def.defaultByTier.basic;
  const packs = def.addonStackable ? opts?.packs ?? 0 : 0;
  const override = opts?.override;

  const raw =
    override !== undefined
      ? override
      : base === UNLIMITED
        ? UNLIMITED
        : base + packs;

  const limit = clampToCeiling(raw, def.systemCeiling);

  const entitlement: Entitlement = { metric, kind: def.kind, limit };

  if (def.kind === 'allowlist') {
    entitlement.allowed = def.allowlistDefaults?.[tier as Tier] ?? def.allowlistDefaults?.basic ?? [];
  }

  return entitlement;
}

/** Resolve every registry metric for a tenant at once. */
export function resolveAllEntitlements(
  tier: string,
  packsByMetric?: Record<string, number>,
  overridesByMetric?: Record<string, number>,
): Record<string, Entitlement> {
  const out: Record<string, Entitlement> = {};
  for (const metric of Object.keys(METRIC_REGISTRY)) {
    out[metric] = resolveEntitlement(metric, tier, {
      packs: packsByMetric?.[metric],
      override: overridesByMetric?.[metric],
    });
  }
  return out;
}

// ───────────────── Stripe entitlement reconstruction (doc §4.2) ─────────────────
//
// Rebuild tier + quota-extension packs from a Stripe subscription's items. The
// hard rule: every quota-granting Stripe object carries `metadata.{metric,amount}`
// (and the base tier item carries `metadata.tier`) — set by setup-stripe-catalog.ts.
// Pure: callers pass a minimal subscription shape (no Stripe SDK dep in shared).

/** Minimal shape of a Stripe subscription item we read. */
export interface StripeSubItemLike {
  quantity?: number | null;
  price?: { metadata?: Record<string, string> | null } | null;
}
export interface StripeSubLike {
  items?: { data?: StripeSubItemLike[] } | null;
}

/** The base tier carried by any item's price `metadata.tier` (undefined for inline price_data). */
export function priceToTier(sub: StripeSubLike): string | undefined {
  for (const item of sub.items?.data ?? []) {
    const t = item.price?.metadata?.tier;
    if (t) return t;
  }
  return undefined;
}

/**
 * Sum quota-extension packs from a subscription's items into a
 * `{ metric: amount }` map. An item is a pack when its price metadata carries
 * `metric` + numeric `amount`; multiple items for the same metric stack, and
 * `amount` is multiplied by the item `quantity`. The base tier item (metric
 * absent) is ignored here. Only `addonStackable` metrics are kept — a stray
 * pack on a non-stackable metric is dropped (defensive).
 */
export function packsFromSubscription(sub: StripeSubLike): Record<string, number> {
  const packs: Record<string, number> = {};
  for (const item of sub.items?.data ?? []) {
    const meta = item.price?.metadata;
    if (!meta?.metric || meta.amount === undefined) continue;
    const def = METRIC_REGISTRY[meta.metric];
    if (!def || !def.addonStackable) continue;
    const amount = Number(meta.amount);
    if (!Number.isFinite(amount)) continue;
    const qty = item.quantity ?? 1;
    packs[meta.metric] = (packs[meta.metric] ?? 0) + amount * qty;
  }
  return packs;
}

/**
 * Reconstruct a tenant's full entitlement set from Stripe (tier + packs) plus
 * any DDB quota overrides (negotiated enterprise limits, NOT reconstructable
 * from Stripe). When the subscription has no catalog tier (enterprise/agency
 * inline price_data), pass `fallbackTier` (the tenant's stored tier).
 *
 * NOTE: the agency `credit` meter is granted via invoice.paid + top-ups (a
 * BALANCE, not a limit), so an agency `credit` pack item here is intentionally
 * NOT turned into a limit — credit packs flow through CreditService, not this.
 */
export function entitlementsFromStripe(
  sub: StripeSubLike,
  fallbackTier: string,
  overridesByMetric?: Record<string, number>,
): { tier: string; packsByMetric: Record<string, number>; entitlements: Record<string, Entitlement> } {
  const tier = priceToTier(sub) ?? fallbackTier;
  const packsByMetric = packsFromSubscription(sub);
  // credit is a balance, not a stackable limit — never let a credit pack inflate
  // a `credit` entitlement "limit".
  delete packsByMetric.credit;
  return {
    tier,
    packsByMetric,
    entitlements: resolveAllEntitlements(tier, packsByMetric, overridesByMetric),
  };
}
